import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useAuth } from "hooks/Auth";
import { useParams, useHistory } from "react-router-dom";
import Container from "Screens/Container";
import { transform as userTransformer, UserObjectSchema } from "schemas/User";
import { transform as licenseTransformer } from "schemas/License";
import { oauthGet, oauthPut, oauthPost } from "helpers/Request";
import { useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import CheckBox from "components/FormElements/CheckBox";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import Text from "components/FormElements/Text";
import Loader from "components/FormElements/Loader";
import SecondaryButton from "components/Buttons/SecondaryButton";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { DuplicateIcon } from "@heroicons/react/outline";
import { DotsHorizontalIcon, ExclamationIcon } from "@heroicons/react/solid";
import { useNotification } from "hooks/Notification";
import { packClasses } from "Util";
import AdminScreen from "Screens/AdminScreen";
import IconMenu from "components/Menus/IconMenu";
import { useModal } from "hooks/Modal";
import {
    removeOauthToken,
    removeOauthRefreshToken
} from "helpers/Auth";
import RolesTable from "./RolesTable";

export default function EditScreen(props) {
    const auth = useAuth();
    const notification = useNotification();
    const modal = useModal();
    const history = useHistory();
    const { uuid } = useParams();
    const location = useLocation();
    const isNew = uuid === "new";
    const [user, setUser] = useState(
        Object.assign({}, UserObjectSchema, { licenses: [auth.publication] })
    );
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showMagicLink, setShowMagicLink] = useState(isNew);
    const [isSaving, setIsSaving] = useState(false);
    const [showGroupInvite, setShowGroupInvite] = useState(false);
    const [forceShowGroupInvite, setForceShowGroupInvite] = useState(false);

    const loadUser = useCallback(() => {
        if (isNew) {
            return;
        }

        setIsLoading(true);

        oauthGet(`/api/v2/user/${uuid}`).then((data) => {
            setUser(userTransformer(data));
            setIsLoading(false);
        });
    }, [uuid, isNew]);

    const requestClipboardWrite = (data, name) => {
        navigator.clipboard.writeText(data).then(() => {
            notification.show(`Copied ${name} to clipboard`);
        });
    };

    const requestImpersonateUser = () => {
        modal.show({
            title: "Impersonate User",
            children: (
                <p className="text-sm text-gray-800">
                    After impersonating a user, you'll need to log out and log
                    back in to return to your account. Do you want to continue?
                </p>
            ),
            confirmText: "Yes, Impersonate",
            onRequestClose: (next) => {
                modal.set({ confirmText: "Finding disguise..." });
                oauthPost(`/api/v2/user/${uuid}/impersonate`).then((data) => {
                    next();
                    setTimeout(() => {
                        removeOauthToken();
                        removeOauthRefreshToken();
                        window.location.href = data.location;
                    }, 1);
                });
            },
        });
    };

    const onSubmit = (values) => {
        if (user && user.uuid === auth.user.uuid) {
            if (!values.is_active) {
                notification.show("You can't deactivate yourself.");
                return;
            }
            if (!values.is_admin) {
                notification.show("You can't demote yourself.");
                return;
            }
        }

        setIsSaving(true);
        const userData = Object.assign(
            {},
            {
                name: values.name,
                email: values.email,
                is_admin: values.is_admin,
                is_active: values.is_active,
                password: values.password.length ? values.password : false,
                passwordConfirmation: values.passwordConfirmation.length
                    ? values.passwordConfirmation
                    : false,
                sendPasswordLink: showMagicLink ? true : false,
            },
            {
                licenses: values.licenses.map((item) => item.value),
            }
        );

        (() => {
            if (!isNew) {
                return oauthPut(`/api/v2/user/${user.uuid}`, userData);
            } else {
                return oauthPost("/api/v2/user", userData);
            }
        })()
            .then((data) => {
                setIsSaving(false);
                notification.show("User saved");
                setShowMagicLink(false);
                if (isNew) {
                    setShowGroupInvite(true);
                    const newUser = userTransformer(data);
                    history.push(`/app/user/${newUser.uuid}`);
                } else {
                    setShowGroupInvite(false);
                }
            })
            .catch((e) => {
                setIsSaving(false);
                notification.show(
                    "There was an error saving. Please try again."
                );
            });
    };

    useEffect(() => {
        setFormData(
            Object.assign(
                {},
                user,
                {
                    password: "",
                    passwordConfirmation: "",
                },
                {
                    licenses: user
                        ? user.licenses.map((item) => {
                              return {
                                  label: item.name,
                                  value: item.uuid,
                              };
                          })
                        : [
                              {
                                  label: auth.publication.name,
                                  value: auth.publication.uuid,
                              },
                          ],
                }
            )
        );
    }, [user, auth.publication]);

    useEffect(() => {
        loadUser();
    }, [loadUser, location]);

    return (
        <AdminScreen>
            <Container>
                <div className="max-w-3xl mx-auto bg-white rounded-md p-4 shadow-sm">
                    <Formik
                        initialValues={formData}
                        enableReinitialize={true}
                        onSubmit={onSubmit}
                    >
                        <Form>
                            <div className="space-y-6">
                                {isLoading ? (
                                    <Loader count={5} />
                                ) : (
                                    <Fragment>
                                        <div className="flex justify-between space-x-4">
                                            <SecondaryButton
                                                label="Back to Users"
                                                type="button"
                                                onClick={() => {
                                                    history.push("/app/user");
                                                }}
                                            />
                                            <div className="space-x-4 flex">
                                                <PrimaryButton
                                                    label="Save User"
                                                    type="submit"
                                                    blocking={isSaving}
                                                    disabled={user.is_snworks}
                                                />
                                                <IconMenu
                                                    variant="secondary"
                                                    icon={
                                                        <DotsHorizontalIcon className="h-5 w-5 text-gray-700" />
                                                    }
                                                    menuItems={[
                                                        <a
                                                            href={`mailto:${user.email}`}
                                                            className="w-full text-left"
                                                        >
                                                            Email User
                                                        </a>,
                                                        <button
                                                            type="button"
                                                            className="w-full text-left"
                                                            onClick={() =>
                                                                requestImpersonateUser()
                                                            }
                                                        >
                                                            Impersonate User
                                                        </button>,
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                        {user.is_snworks && (
                                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                                <div className="flex">
                                                    <div className="flex-shrink-0">
                                                        <ExclamationIcon
                                                            className="h-5 w-5 text-yellow-400"
                                                            aria-hidden="true"
                                                        />
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm text-yellow-700">
                                                            SNworks users cannot
                                                            be modified via the
                                                            admin interface.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {showGroupInvite && (
                                            <div className="bg-green-50 border-l-4 border-green-400 p-4">
                                                Make sure to{" "}
                                                <button
                                                    type="button"
                                                    className="font-bold text-green-600 underline"
                                                    onClick={() => {
                                                        setForceShowGroupInvite(
                                                            true
                                                        );
                                                    }}
                                                >
                                                    add this user to groups
                                                </button>{" "}
                                                in order to give them access to
                                                content.
                                            </div>
                                        )}

                                        <div
                                            className={packClasses(
                                                "flex w-full items-end space-x-4",
                                                isNew && "hidden"
                                            )}
                                        >
                                            <div className="grow">
                                                <TextInput
                                                    name="uuid"
                                                    label="UUID"
                                                    readOnly
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="p-1.5 bg-gray-200 shadow-sm rounded grow-0"
                                            >
                                                <DuplicateIcon
                                                    className="h-6 w-6 text-gray-600"
                                                    onClick={() => {
                                                        requestClipboardWrite(
                                                            user.uuid,
                                                            "UUID"
                                                        );
                                                    }}
                                                />
                                            </button>
                                        </div>
                                        <TextInput name="name" label="Name" />
                                        <div className="flex w-full items-end space-x-4">
                                            <div className="grow">
                                                <TextInput
                                                    name="email"
                                                    label="Email"
                                                    type="email"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="p-1.5 bg-gray-200 shadow-sm rounded grow-0"
                                            >
                                                <DuplicateIcon
                                                    className="h-6 w-6 text-gray-600"
                                                    onClick={() => {
                                                        requestClipboardWrite(
                                                            user.email,
                                                            "email"
                                                        );
                                                    }}
                                                />
                                            </button>
                                        </div>

                                        <hr />

                                        <CheckBox
                                            label="Active User"
                                            id="is_active"
                                            name="is_active"
                                            helpertext="Inactive users cannot login or manage any settings"
                                        />
                                        <CheckBox
                                            label="Site Admin"
                                            id="is_admin"
                                            name="is_admin"
                                            helpertext="Site admins have full control over accounts and publication settings"
                                        />

                                        <hr />

                                        {showMagicLink ? (
                                            <div className="space-y-2">
                                                <Text label="Password">
                                                    A magic password link will
                                                    be sent to this user.
                                                </Text>
                                                <SecondaryButton
                                                    label="Manually set password"
                                                    onClick={() =>
                                                        setShowMagicLink(false)
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <TextInput
                                                    type="password"
                                                    id="password"
                                                    name="password"
                                                    label="Password"
                                                />
                                                <TextInput
                                                    type="password"
                                                    id="passwordConfirmation"
                                                    name="passwordConfirmation"
                                                    label="Password Confirmation"
                                                />
                                                <SecondaryButton
                                                    type="button"
                                                    label="Send magic password link"
                                                    onClick={() =>
                                                        setShowMagicLink(true)
                                                    }
                                                />
                                            </div>
                                        )}

                                        <hr />
                                        {!user.is_snworks && (
                                            <AutocompleteInput
                                                label="Publications"
                                                id="licenses"
                                                name="licenses"
                                                onRequestSearch={async (
                                                    term
                                                ) => {
                                                    let items = await oauthGet(
                                                        "/api/v2/license",
                                                        {
                                                            keyword: term,
                                                        }
                                                    );
                                                    return licenseTransformer(
                                                        items
                                                    ).map((item) => {
                                                        return {
                                                            label: item.name,
                                                            value: item.uuid,
                                                        };
                                                    });
                                                }}
                                            />
                                        )}

                                        {user.is_snworks && (
                                            <div className="text-sm test-gray-900">
                                                SNworks users do not require
                                                group or license assignments.
                                            </div>
                                        )}

                                        <hr />
                                        {user.id &&
                                            user.roles &&
                                            !user.is_snworks && (
                                                <RolesTable
                                                    user={user}
                                                    showGroupInvite={
                                                        forceShowGroupInvite
                                                    }
                                                />
                                            )}

                                        <div className="text-right space-x-4">
                                            <PrimaryButton
                                                label="Save User"
                                                type="submit"
                                                blocking={isSaving}
                                                disabled={user.is_snworks}
                                            />
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </Form>
                    </Formik>
                </div>
            </Container>
        </AdminScreen>
    );
}
